import React, { useEffect, useState, useRef } from "react";
import {
  TextField,
  ListItemText,
  Button,
  DialogTitle,
  DialogContent,
  Divider as MuiDivider,
  IconButton,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import {
  ChatDialog,
  FloatingChatButton,
  MessageItem,
  MessageList,
} from "./styles";
import { updateDictationHoldMessage } from "apiHandler/actions";
import { useUserData } from "components/customHooks/userDataContext";

const DictationMessage = ({
  onCloseChat,
  dictation_id,
  messages,
  setMessages,
}) => {
  const [chatOpen, setChatOpen] = useState(false);

  const [inputText, setInputText] = useState("");

  const [sending, setSending] = useState(false);
  const userData = useUserData();
  const token = userData?.access_token;
  const currentUserEmail = userData?.email;
  const messagesEndRef = useRef(null);

  // Fetch messages when chat opens

  // Fetch messages when chat opens

  const handleChatToggle = () => {
    setChatOpen(!chatOpen);
    if (!chatOpen && onCloseChat) {
      onCloseChat();
    }
  };

  const sendMessage = async () => {
    if (!inputText.trim() || !dictation_id || !token || sending) return;

    const tempMessageId = Date.now();
    const messageText = inputText.trim();

    // Optimistic UI update
    const optimisticMessage = {
      id: tempMessageId,
      text: messageText,
      isUser: true,
      created_date: new Date().toLocaleString(),
      user: currentUserEmail,
      user_type: "Provider",
    };

    setMessages((prev) => [...prev, optimisticMessage]);
    setInputText("");
    setSending(true);

    try {
      const payload = {
        type: "Scribe",
        search_criteria: {
          scribe_id: currentUserEmail,
          dictation_id: dictation_id,
          status: "On-Hold",
          message: messageText,
        },
      };

      await updateDictationHoldMessage(payload, token);
    } catch (error) {
      console.log("Error sending message: ", error);

      // Remove optimistic message on error
      setMessages((prev) => prev.filter((msg) => msg.id !== tempMessageId));
      alert("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <>
      <FloatingChatButton
        color="primary"
        onClick={handleChatToggle}
        title="Chat Support"
      >
        <ChatIcon />
      </FloatingChatButton>

      <ChatDialog
        open={chatOpen}
        onClose={handleChatToggle}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Patient Messages
          <IconButton onClick={handleChatToggle} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MessageList>
            {messages.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 200,
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No messages yet
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Start the conversation
                </Typography>
              </Box>
            ) : (
              messages.map((message) => (
                <MessageItem key={message.id} isUser={message.isUser}>
                  <ListItemText
                    primary={message.text}
                    secondary={
                      message.isUser
                        ? "You"
                        : `${message.user_type}: ${message.user}`
                    }
                    sx={{
                      "& .MuiListItemText-secondary": {
                        fontSize: "0.75rem",
                        opacity: 0.7,
                      },
                    }}
                  />
                </MessageItem>
              ))
            )}
            <div ref={messagesEndRef} />
          </MessageList>
          <MuiDivider />
          <Box sx={{ p: 2, display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Type your message..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyPress}
              multiline
              maxRows={3}
              disabled={sending}
            />
            <Button
              variant="contained"
              onClick={sendMessage}
              disabled={!inputText.trim() || sending}
              endIcon={
                sending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
            >
              {sending ? "Sending..." : "Send"}
            </Button>
          </Box>
        </DialogContent>
      </ChatDialog>
    </>
  );
};

export default DictationMessage;
